// ─── Enumerations ─────────────────────────────────────────────────────────────

export type VendorStatus = 'Active' | 'Inactive' | 'Pending' | 'Archived';

export type VendorCategory = 'Accounts Payable' | 'Tech/IT' | 'Operations' | 'Truss' | 'Contracts';

export type BudgetType = 'Annual' | 'PerContract';

export type ThresholdType = 'Ceiling' | 'Awareness';

export type VendorRole = 'VendorManager' | 'DeptManager' | 'Viewer';

export type AuthStatus = 'PendingVM' | 'PendingDM' | 'PendingTee' | 'Executed' | 'Rejected';

export type BillingAuthStatus = 'PendingDM' | 'Approved' | 'Rejected';

export type NotificationType = 'ExpirationAlert' | 'ContractPendingApproval' | 'BillingPendingApproval';

// ─── Sub-shapes ────────────────────────────────────────────────────────────────

export interface ContactInfo {
  name: string;
  title: string;
  phone: string;
  email: string;
}

export interface BillingAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface Budget {
  amount: number;
  type: BudgetType;
}

export interface SpendThreshold {
  amount: number;
  type: ThresholdType; // Ceiling = do not exceed; Awareness = notify only
}

export interface AuthEvent {
  step: 'VMReview' | 'DMApproval' | 'TeeSignature';
  status: 'Pending' | 'Approved' | 'Rejected';
  actor: string; // email
  timestamp: string;
  notes?: string;
}

// ─── Core entities ─────────────────────────────────────────────────────────────

export interface Vendor {
  id: string;
  name: string;
  website?: string;
  status: VendorStatus;
  categories: VendorCategory[];
  parentVendorId?: string;
  rollupSpendToParent?: boolean; // if true, this vendor's spend is included in parent's totalSpend

  primaryContact: ContactInfo;
  billingContact: ContactInfo;
  billingAddress: BillingAddress;

  budget?: Budget;
  spendThreshold?: SpendThreshold;

  // Optional fields
  vendorId?: string;            // internal vendor ID
  w9OnFile: boolean;
  insuranceCertOnFile: boolean;
  comments?: string;

  totalSpend: number;           // computed from approved BillingEntries
  createdAt: string;
  updatedAt: string;
}

export interface MSA {
  id: string;
  vendorId: string;
  effectiveDate?: string;
  expirationDate?: string;
  autoRenewal: boolean;
  documentBase64?: string;
  documentFileName?: string;
  authStatus: AuthStatus;
  authHistory: AuthEvent[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SOW {
  id: string;
  vendorId: string;
  msaId?: string;              // optional link to parent MSA
  sowNumber: string;           // e.g. "SOW-2026-001"
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  dollarValue?: number;
  budget?: Budget;
  documentBase64?: string;
  documentFileName?: string;
  authStatus: AuthStatus;
  authHistory: AuthEvent[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingEntry {
  id: string;
  vendorId: string;
  sowId?: string;
  type: 'Invoice' | 'Milestone';
  amount: number;
  description: string;
  invoiceRef?: string;
  entryDate: string;
  loggedBy: string;            // Vendor Manager email
  authStatus: BillingAuthStatus;
  authHistory: AuthEvent[];
  createdAt: string;
}

export interface Notification {
  id: string;
  type: NotificationType;
  referenceId: string;         // MSA / SOW / BillingEntry id
  referenceType: 'MSA' | 'SOW' | 'BillingEntry';
  vendorId: string;
  message: string;
  daysUntil?: number;          // for expiration alerts
  read: boolean;
  createdAt: string;
  mailtoHref?: string;         // pre-crafted Outlook mailto link
}

// ─── UI-only helpers ──────────────────────────────────────────────────────────

export interface VendorWithChildren extends Vendor {
  children?: Vendor[];
  msas?: MSA[];
  sows?: SOW[];
}

export const ALL_CATEGORIES: VendorCategory[] = [
  'Accounts Payable',
  'Tech/IT',
  'Operations',
  'Truss',
  'Contracts',
];

export const VENDOR_STATUSES: VendorStatus[] = ['Active', 'Inactive', 'Pending', 'Archived'];
